import { Box, Stack } from "@mui/material";
import { SchoolRounded, ApartmentRounded } from "@mui/icons-material";
import { Custom, Icon } from "components";
import { useApp, useTheme } from "contexts";
import { ActivityType, ActivityJourneyItemType } from "types";
import { Combines, Conversions } from "helpers";
import { EnumTheme } from "utils/enums";

type Props = {
  item: ActivityType;
  head: boolean;
  tail: boolean;
  hasCalculation?: boolean;
};

export const Activity = ({
  hasCalculation = false,
  ...props
}: Props) => {
  const { locale, t } = useApp();
  const { theme } = useTheme();
  const isDark = theme.id === EnumTheme.Dark;

  const isEdu = props.item.is === "education";
  const hasJourney = props.item.journey && props.item.journey.length > 0;

  const calculateDuration = (
    from: string,
    to: string = new Date().toDateString()
  ) => {
    const start = new Date(from);
    const end = new Date(to);
    const diff = end.getTime() - start.getTime();
    const months = Math.floor(diff / (1000 * 60 * 60 * 24 * 30));
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;

    return years === 0
      ? Combines.interpolate(
          remainingMonths === 1
            ? t.date.timespan.month.singular
            : t.date.timespan.month.plural,
          { count: remainingMonths }
        )
      : remainingMonths === 0
      ? Combines.interpolate(
          years === 1
            ? t.date.timespan.year.singular
            : t.date.timespan.year.plural,
          { count: years }
        )
      : `${Combines.interpolate(
          years === 1
            ? t.date.timespan.year.singular
            : t.date.timespan.year.plural,
          { count: years }
        )} ${t.conjunction.and} ${Combines.interpolate(
          remainingMonths === 1
            ? t.date.timespan.month.singular
            : t.date.timespan.month.plural,
          { count: remainingMonths }
        )}`;
  };

  const currentLocaleKey = locale.toLowerCase();

  return (
    <Box sx={{ m: theme.spacing.xl, position: "relative" }}>
      <Box
        sx={{
          p: { xs: theme.spacing.lg, md: theme.spacing.xl },
          backgroundColor: theme.color.background.accent,
          border: `1px solid ${theme.color.border.color}`,
          boxShadow: `3px 3px 0px 0px ${theme.color.border.color}`,
          borderRadius: theme.border.square,
          transition: "transform 0.15s ease, box-shadow 0.15s ease",
          "&:hover": {
            transform: "translate(-2px, -2px)",
            boxShadow: `5px 5px 0px 0px ${isDark ? "#000000" : "#191b22"}`,
          },
          "&:active": {
            transform: "translate(2px, 2px)",
            boxShadow: "none",
          },
        }}
      >
        <Stack direction="column" spacing={theme.spacing.md}>
          {/* Company / Entity Main Header Row */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={1}
          >
            <Stack direction="row" alignItems="center" spacing={theme.spacing.sm}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: theme.color.theme.color,
                  border: `1px solid ${theme.color.border.color}`,
                  color: theme.color.primary.color,
                }}
              >
                {isEdu ? (
                  <SchoolRounded sx={{ fontSize: theme.font.md }} />
                ) : (
                  <ApartmentRounded sx={{ fontSize: theme.font.md }} />
                )}
              </Box>
              <Box>
                <Custom.Typography
                  variant="h3"
                  weight={theme.font.bold}
                  sx={{
                    fontSize: { xs: theme.font.md, md: theme.font.lg },
                    color: theme.color.font.color,
                  }}
                >
                  {props.item.at}
                </Custom.Typography>
                <Custom.Typography
                  variant="body2"
                  sx={{
                    color: theme.color.font.accent,
                    fontSize: theme.font.xs,
                  }}
                >
                  {props.item.in[currentLocaleKey]} •{" "}
                  {Conversions.formattedDate(
                    new Date(props.item.from),
                    currentLocaleKey
                  )}{" "}
                  —{" "}
                  {props.item.to
                    ? Conversions.formattedDate(
                        new Date(props.item.to),
                        currentLocaleKey
                      )
                    : t.label.present}{" "}
                  {hasCalculation &&
                    `(${calculateDuration(props.item.from, props.item.to)})`}
                </Custom.Typography>
              </Box>
            </Stack>

            {/* Overall Type Badge */}
            <Box
              sx={{
                px: theme.spacing.xs,
                py: theme.spacing.xxs,
                fontFamily: '"Space Mono", monospace',
                fontSize: theme.font.xxs,
                fontWeight: theme.font.bold,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                backgroundColor: isEdu
                  ? theme.color.secondary.color
                  : theme.color.theme.color,
                color: isEdu
                  ? theme.color.secondary.text
                  : theme.color.font.color,
                border: `1px solid ${theme.color.border.color}`,
                borderRadius: theme.border.square,
              }}
            >
              {((props.item.type[currentLocaleKey] || props.item.type.en) as string).toUpperCase()}
            </Box>
          </Stack>

          {/* Stepped Journey Section integrated directly inside the outer tile */}
          {hasJourney ? (
            <Box sx={{ mt: theme.spacing.sm }}>
              {props.item.journey!.map((step: ActivityJourneyItemType, stepIdx: number) => {
                const isCurrent = stepIdx === 0;
                const totalSteps = props.item.journey!.length;
                const isLast = stepIdx === totalSteps - 1;
                const stageNumber = totalSteps - stepIdx;

                return (
                  <Box
                    key={stepIdx}
                    sx={{
                      position: "relative",
                      display: "flex",
                      alignItems: "stretch",
                      pt: stepIdx === 0 ? theme.spacing.sm : theme.spacing.md,
                      pb: isLast ? 0 : theme.spacing.md,
                    }}
                  >
                    {/* Retro Waypoint Column: Beacon Node + Stepped Circuit Trace */}
                    <Box
                      sx={{
                        width: 32,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        flexShrink: 0,
                        mr: { xs: theme.spacing.sm, md: theme.spacing.md },
                      }}
                    >
                      {/* Waypoint Diamond / Square Node */}
                      <Box
                        sx={{
                          width: 22,
                          height: 22,
                          backgroundColor: isCurrent
                            ? theme.color.primary.color
                            : theme.color.theme.color,
                          border: `2px solid ${
                            isCurrent
                              ? theme.color.primary.accent
                              : theme.color.border.color
                          }`,
                          borderRadius: theme.border.square,
                          boxShadow: isCurrent
                            ? `0 0 8px ${theme.color.primary.color}88, 2px 2px 0px 0px ${
                                isDark ? "#000000" : "#191b22"
                              }`
                            : `1px 1px 0px 0px ${theme.color.border.color}`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          zIndex: 2,
                          flexShrink: 0,
                          transition: "all 0.2s ease",
                        }}
                      >
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            backgroundColor: isCurrent
                              ? theme.color.primary.text
                              : theme.color.font.accent,
                            borderRadius: theme.border.square,
                          }}
                        />
                      </Box>

                      {/* Stepped Connection Conduit / Circuit Trace */}
                      {!isLast && (
                        <Box
                          sx={{
                            flex: 1,
                            width: "2px",
                            mt: theme.spacing.xxs,
                            background: isCurrent
                              ? `repeating-linear-gradient(to bottom, ${theme.color.primary.color} 0px, ${theme.color.primary.color} 6px, transparent 6px, transparent 12px)`
                              : `repeating-linear-gradient(to bottom, ${theme.color.border.color} 0px, ${theme.color.border.color} 5px, transparent 5px, transparent 10px)`,
                            opacity: isCurrent ? 1 : 0.7,
                          }}
                        />
                      )}
                    </Box>

                    {/* Milestone Content Area */}
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        pb: isLast ? 0 : theme.spacing.sm,
                        borderBottom: isLast
                          ? "none"
                          : `1px solid ${theme.color.border.color}`,
                      }}
                    >
                      {/* Milestone Header Row */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          flexWrap: "wrap",
                          gap: theme.spacing.xs,
                          mb: theme.spacing.xs,
                        }}
                      >
                        {/* Milestone Level / Stage Pill */}
                        <Box
                          sx={{
                            px: theme.spacing.xs,
                            py: theme.spacing.xxs / 2,
                            backgroundColor: isCurrent
                              ? theme.color.primary.color
                              : theme.color.theme.color,
                            color: isCurrent
                              ? theme.color.primary.text
                              : theme.color.font.accent,
                            border: `1px solid ${
                              isCurrent
                                ? theme.color.primary.color
                                : theme.color.border.color
                            }`,
                            borderRadius: theme.border.square,
                            fontFamily: '"Space Mono", monospace',
                            fontSize: theme.font.xxxs,
                            fontWeight: theme.font.bold,
                            letterSpacing: "0.08em",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <Box
                            component="span"
                            sx={{
                              width: 5,
                              height: 5,
                              backgroundColor: isCurrent
                                ? theme.color.primary.text
                                : theme.color.font.accent,
                            }}
                          />
                          <span>
                            {isCurrent
                              ? t.label?.current_stage || "CURRENT LEVEL"
                              : `${t.label?.growth_path || "STAGE"} // 0${stageNumber}`}
                          </span>
                        </Box>

                        {/* Dates */}
                        <Custom.Typography
                          variant="body2"
                          sx={{
                            color: isCurrent
                              ? theme.color.primary.color
                              : theme.color.font.accent,
                            fontSize: theme.font.xxs,
                            fontFamily: '"Space Mono", monospace',
                            fontWeight: isCurrent
                              ? theme.font.bold
                              : theme.font.normal,
                          }}
                        >
                          {Conversions.formattedDate(
                            new Date(step.from),
                            currentLocaleKey
                          )}{" "}
                          —{" "}
                          {step.to
                            ? Conversions.formattedDate(
                                new Date(step.to),
                                currentLocaleKey
                              )
                            : t.label.present}{" "}
                          {hasCalculation &&
                            `(${calculateDuration(step.from, step.to)})`}
                        </Custom.Typography>
                      </Box>

                      {/* Role Title */}
                      <Custom.Typography
                        variant="h4"
                        weight={theme.font.bold}
                        sx={{
                          fontSize: { xs: theme.font.sm, md: theme.font.md },
                          color: isCurrent
                            ? theme.color.font.color
                            : theme.color.font.color,
                          mb: theme.spacing.xs,
                        }}
                      >
                        {step.as[currentLocaleKey]}
                      </Custom.Typography>

                      {/* Role Description */}
                      <Custom.Typography
                        variant="body2"
                        sx={{
                          color: theme.color.font.accent,
                          lineHeight: 2.1,
                          fontSize: theme.font.sm,
                          mb: theme.spacing.sm,
                        }}
                      >
                        {step.briefdescription[currentLocaleKey]}
                      </Custom.Typography>

                      {/* Step Tech Badges */}
                      {step.techs && step.techs.length > 0 && (
                        <Stack direction="row" flexWrap="wrap" gap={theme.spacing.xs}>
                          {step.techs.map((techItem: string) => (
                            <Box
                              key={techItem}
                              sx={{
                                px: theme.spacing.xs,
                                py: theme.spacing.xxs,
                                backgroundColor: theme.color.theme.color,
                                border: `1px solid ${theme.color.border.color}`,
                                borderRadius: theme.border.square,
                                fontFamily: '"Space Mono", monospace',
                                fontSize: theme.font.xxxs,
                                fontWeight: theme.font.semiBold,
                                color: theme.color.font.accent,
                                display: "inline-flex",
                                alignItems: "center",
                                gap: theme.spacing.xxs,
                              }}
                            >
                              <Icon.Svg size={12} name={techItem} />
                              <span>{techItem}</span>
                            </Box>
                          ))}
                        </Stack>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ) : (
            /* Single Role Standard Display */
            <Box>
              <Custom.Typography
                variant="h4"
                weight={theme.font.bold}
                sx={{
                  fontSize: { xs: theme.font.sm, md: theme.font.md },
                  color: theme.color.primary.color,
                  mb: theme.spacing.xs,
                }}
              >
                {props.item.as[currentLocaleKey]}
              </Custom.Typography>

              <Custom.Typography
                variant="body2"
                sx={{
                  color: theme.color.font.accent,
                  lineHeight: 2.1,
                  fontSize: theme.font.sm,
                  mb: theme.spacing.sm,
                }}
              >
                {props.item.briefdescription[currentLocaleKey]}
              </Custom.Typography>

              {props.item.techs && props.item.techs.length > 0 && (
                <Stack
                  direction="row"
                  flexWrap="wrap"
                  gap={theme.spacing.xs}
                  sx={{
                    pt: theme.spacing.sm,
                  }}
                >
                  {props.item.techs.map((item: string) => (
                    <Box
                      key={item}
                      sx={{
                        px: theme.spacing.xs,
                        py: theme.spacing.xxs,
                        backgroundColor: theme.color.theme.color,
                        border: `1px solid ${theme.color.border.color}`,
                        borderRadius: theme.border.square,
                        fontFamily: '"Space Mono", monospace',
                        fontSize: theme.font.xxxs,
                        fontWeight: theme.font.semiBold,
                        color: theme.color.font.accent,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: theme.spacing.xxs,
                      }}
                    >
                      <Icon.Svg size={14} name={item} />
                      <span>{item}</span>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>
          )}
        </Stack>
      </Box>
    </Box>
  );
};